import * as THREE from "three";
import type { Tool, ToolContext, PointerInfo } from "./Tool";

/**
 * Owns the active tool, routes DOM pointer events through the common
 * PointerInfo wrapper, and toggles OrbitControls according to the tool's
 * grabsPointer flag. Any tool dropped into `register` just works.
 */
export class ToolManager {
  private readonly tools = new Map<string, Tool>();
  private readonly order: string[] = [];
  private active: Tool | null = null;
  private ndc = new THREE.Vector2();
  private raycaster = new THREE.Raycaster();
  private dragging = false;
  private listeners = new Set<() => void>();

  constructor(private readonly ctx: ToolContext, private readonly canvas: HTMLCanvasElement) {
    canvas.addEventListener("pointerdown", this.handleDown);
    canvas.addEventListener("pointermove", this.handleMove);
    window.addEventListener("pointerup", this.handleUp);
    window.addEventListener("keydown", this.handleKey);
  }

  register(tool: Tool): void {
    this.tools.set(tool.id, tool);
    this.order.push(tool.id);
  }

  listTools(): Tool[] {
    return this.order.map((id) => this.tools.get(id)!).filter(Boolean);
  }

  activate(id: string): void {
    const next = this.tools.get(id);
    if (!next || next === this.active) return;
    if (this.active?.onDeactivate) this.active.onDeactivate(this.ctx);
    this.active = next;
    this.ctx.controls.enabled = !next.grabsPointer;
    this.ctx.setHint(next.hint);
    if (next.onActivate) next.onActivate(this.ctx);
    this.emit();
  }

  getActive(): Tool | null {
    return this.active;
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    this.listeners.forEach((fn) => fn());
  }

  private updateNdc(ev: PointerEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    this.ndc.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
  }

  private buildInfo(ev: PointerEvent): PointerInfo {
    this.updateNdc(ev);
    this.raycaster.setFromCamera(this.ndc, this.ctx.camera);
    const hit = new THREE.Vector3();
    const ok = this.raycaster.ray.intersectPlane(this.ctx.drawingPlane, hit);
    return {
      event: ev,
      ndc: this.ndc.clone(),
      raycaster: this.raycaster,
      planePoint: ok ? hit : null,
      button: ev.button,
    };
  }

  private handleDown = (ev: PointerEvent): void => {
    if (!this.active) return;
    // Only the primary (left) button drives tools; secondary/middle are for OrbitControls.
    if (ev.button !== 0) return;
    const info = this.buildInfo(ev);
    if (this.active.grabsPointer) {
      this.canvas.setPointerCapture(ev.pointerId);
      this.dragging = true;
    }
    this.active.onPointerDown?.(info, this.ctx);
  };

  private handleMove = (ev: PointerEvent): void => {
    if (!this.active) return;
    const info = this.buildInfo(ev);
    this.active.onPointerMove?.(info, this.ctx);
  };

  private handleUp = (ev: PointerEvent): void => {
    if (!this.active) return;
    const info = this.buildInfo(ev);
    if (this.dragging) {
      try {
        this.canvas.releasePointerCapture(ev.pointerId);
      } catch {
        /* pointer may not be captured; ignore */
      }
      this.dragging = false;
    }
    this.active.onPointerUp?.(info, this.ctx);
  };

  private handleKey = (ev: KeyboardEvent): void => {
    if ((ev.target as HTMLElement | null)?.tagName === "INPUT") return;
    if (!this.active) return;
    this.active.onKeyDown?.(ev, this.ctx);
  };
}
